import { useAuth } from '../contexts/auth-context';
import { Button, Menu, MenuItem, MenuSeparator, MenuLabel, Icon, cn } from './ui';

// Top bar. Four destinations instead of six: Volgend folded into Baan as a
// filter, and Mijn ritten now carries the PR's and badges that used to sit in
// Profiel.
//
// Accessibility: the nav is a real <nav> with aria-current on the active item,
// there is a skip link to the main content, and the frosted background keeps a
// solid fallback colour for browsers without backdrop-filter.

const NAV = [
  { view: 'home', label: 'Baan' },
  { view: 'live', label: 'Live' },
  { view: 'ranking', label: 'Ranglijst' },
  { view: 'myseason', label: 'Mijn ritten', auth: 'transponders' },
  { view: 'profile', label: 'Profiel' },
];

export default function Header({ view, onNavigate }) {
  const { loggedIn, user, transponders, login, logout } = useAuth();
  const hasTransponders = loggedIn && transponders.length > 0;

  const items = NAV.filter(n => n.auth !== 'transponders' || hasTransponders);

  return (
    <header
      className={cn(
        'site-header sticky top-0 z-[100] border-b border-line',
        'bg-[rgba(255,255,255,0.78)] backdrop-blur-[14px] supports-[not(backdrop-filter:blur(0))]:bg-surface',
      )}
    >
      <a
        href="#main"
        className={cn(
          'sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[110]',
          'focus:rounded-pill focus:bg-primary-600 focus:px-4 focus:py-2 focus:text-white',
        )}
      >
        Naar hoofdinhoud
      </a>

      <div className="header-inner mx-auto flex min-h-[72px] max-w-[1200px] items-center justify-between gap-4 px-5 py-2">
        <button
          className="flex min-h-12 shrink-0 cursor-pointer items-center gap-3 rounded-md p-0 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-500"
          onClick={() => onNavigate('home')}
          aria-label="Naar de baan"
        >
          <img
            src="https://bvalkmaar3e4ba76ccd.blob.core.windows.net/wordpress-uploads/2026/04/cropped-logo_bva_2024-high.png"
            alt="BVA IJsbaan De Meent"
            className="block h-[38px] w-auto"
            onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = '/favicon-192.png';
            }}
          />
          <span className="brand-wordmark">
            <strong>SCHAATSEN</strong>
            <span>DE MEENT / ALKMAAR</span>
          </span>
        </button>

        <nav className="header-nav flex flex-1 items-center gap-1" aria-label="Hoofdnavigatie">
          {items.map(item => (
            <NavBtn key={item.view} active={view === item.view || (item.view === 'profile' && view === 'coach')} onClick={() => onNavigate(item.view)}>
              {item.label}
            </NavBtn>
          ))}
        </nav>

        <div className="header-account shrink-0">
          {loggedIn ? (
            <Menu
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="max-w-[190px]"
                  aria-label={`Account: ${user?.name || user?.email}`}
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-pill bg-primary-100 font-mono text-[13px] font-bold text-primary-700">
                    {initialsOf(user)}
                  </span>
                  <span className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {user?.name || user?.email}
                  </span>
                </Button>
              }
            >
              <MenuLabel>{user?.email}</MenuLabel>
              <MenuItem icon={<Icon name="compass" size={15} />} onSelect={() => onNavigate('profile')}>
                Profiel
              </MenuItem>
              {hasTransponders && (
                <MenuItem icon={<Icon name="calendar" size={15} />} onSelect={() => onNavigate('myseason')}>
                  Mijn ritten
                </MenuItem>
              )}
              <MenuItem icon={<Icon name="compass" size={15} />} onSelect={() => onNavigate('coach')}>
                Coachwerkplek
              </MenuItem>
              <MenuSeparator />
              <MenuItem icon={<Icon name="lock" size={15} />} onSelect={logout}>
                Uitloggen
              </MenuItem>
            </Menu>
          ) : (
            <Button variant="accent" size="sm" onClick={login}>
              Inloggen
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}

function initialsOf(user) {
  const src = user?.name || user?.email || '?';
  const parts = src.trim().split(/[\s@._-]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '?').toUpperCase() + (parts[1]?.[0] ?? '').toUpperCase();
}

function NavBtn({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex min-h-11 cursor-pointer items-center rounded-md px-3',
        'font-display text-[16px] font-semibold tracking-[0.01em]',
        'transition-[background-color,color] duration-150',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
        active ? 'bg-primary-600 text-white' : 'text-ink-2 hover:bg-surface-3 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
